import { useState, useEffect, useRef } from "react";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { 
  CreditCard, Plus, Trash2, Loader2, Receipt, RefreshCw, AlertCircle
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import "./Batch6Billing.css";

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Billing Card Form Component
const BillingCardForm = ({ clientSecret, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    try {
      const { setupIntent, error: stripeError } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
        },
      });

      if (stripeError) {
        setError(stripeError.message);
        setProcessing(false);
        return;
      }

      if (setupIntent.status === 'succeeded') {
        onSuccess(setupIntent.payment_method);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="border border-slate-200 rounded-xl p-4 bg-white">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#1e293b',
                '::placeholder': { color: '#94a3b8' },
              },
              invalid: { color: '#ef4444' },
            },
          }}
        />
      </div>
      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
      <div className="flex gap-3 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="flex-1 rounded-xl"
          disabled={processing}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          className="flex-1 bg-slate-900 text-white hover:bg-slate-800 rounded-xl"
          disabled={processing || !stripe}
        >
          {processing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <CreditCard className="w-4 h-4 mr-2" />
              Save Card
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

const BillingTab = ({ token, API }) => {
  const billingRoot = useRef(null);
  const billingOpener = useRef(null);
  const [billingPaymentMethods, setBillingPaymentMethods] = useState([]);
  const [loadingBillingMethods, setLoadingBillingMethods] = useState(false);
  const [addingBillingCard, setAddingBillingCard] = useState(false);
  const [billingSetupClientSecret, setBillingSetupClientSecret] = useState(null);
  const [autoPayEnabled, setAutoPayEnabled] = useState(false);
  const [autoPayLoading, setAutoPayLoading] = useState(false);
  const [autoPayDefaultCard, setAutoPayDefaultCard] = useState(null);

  const fetchBillingPaymentMethods = async () => {
    setLoadingBillingMethods(true);
    try {
      const response = await axios.get(`${API}/payments/org/billing/payment-methods`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBillingPaymentMethods(response.data.payment_methods || []);
    } catch (error) {
      console.error("Error fetching billing methods:", error);
    } finally {
      setLoadingBillingMethods(false);
    }
  };

  const fetchAutoPayStatus = async () => {
    try {
      const response = await axios.get(`${API}/invoices/org/auto-pay/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAutoPayEnabled(response.data.auto_pay_enabled || false);
      setAutoPayDefaultCard(response.data.default_payment_method || null);
    } catch (error) {
      console.error("Error fetching auto-pay status:", error);
    }
  };

  const handleAddBillingCard = async () => {
    setAddingBillingCard(true);
    try {
      const response = await axios.get(`${API}/payments/org/billing/setup-intent`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBillingSetupClientSecret(response.data.client_secret);
    } catch (error) {
      console.error("Error creating setup intent:", error);
      toast.error("Failed to initialize card setup");
      setAddingBillingCard(false);
    }
  };

  const handleSetDefaultBillingCard = async (paymentMethodId) => {
    try {
      await axios.post(
        `${API}/payments/org/billing/payment-methods/${paymentMethodId}/default`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Default payment method updated");
      fetchBillingPaymentMethods();
    } catch (error) {
      console.error("Error setting default:", error);
      toast.error("Failed to update default payment method");
    }
  };

  const handleDeleteBillingCard = async (paymentMethodId) => {
    if (!confirm("Are you sure you want to remove this payment method?")) return;
    try {
      await axios.delete(`${API}/payments/org/billing/payment-methods/${paymentMethodId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Payment method removed");
      fetchBillingPaymentMethods();
    } catch (error) {
      console.error("Error deleting card:", error);
      toast.error("Failed to remove payment method");
    }
  };

  const handleToggleAutoPay = async (enabled) => {
    setAutoPayLoading(true);
    try {
      const response = await axios.post(
        `${API}/invoices/org/auto-pay/toggle`,
        { enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAutoPayEnabled(response.data.auto_pay_enabled);
      toast.success(response.data.message);
    } catch (error) {
      console.error("Error toggling auto-pay:", error);
      toast.error(error.response?.data?.detail || "Failed to update auto-pay setting");
    } finally {
      setAutoPayLoading(false);
    }
  };

  // Fetch data on mount
  useEffect(() => {
    fetchBillingPaymentMethods();
    fetchAutoPayStatus();
  }, [token]);

  return (
    <div ref={billingRoot} role="region" aria-label="Billing settings" tabIndex={-1} className="sh-org-billing-settings-batch6 space-y-6">
      <section className="billing-section billing-methods" aria-labelledby="billing-methods-title">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h3 id="billing-methods-title" className="billing-title">Payment Methods</h3>
            <p className="text-sm text-slate-500 mt-1">
              Manage payment methods for paying invoices and subscription fees
            </p>
          </div>
          <div className="w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center">
            <Receipt className="w-6 h-6 text-emerald-600" />
          </div>
        </div>

        {/* Add Payment Method Button */}
        <Button
          onClick={(event) => { billingOpener.current = event.currentTarget; handleAddBillingCard(); }}
          disabled={addingBillingCard}
          className="mb-6 rounded-full bg-slate-900 text-white hover:bg-slate-800"
          data-testid="add-billing-card-btn"
        >
          {addingBillingCard ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Plus className="w-4 h-4 mr-2" />
          )}
          Add Payment Method
        </Button>

        {/* Payment Methods List */}
        {loadingBillingMethods ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : billingPaymentMethods.length === 0 ? (
          <div className="text-center py-12 bg-slate-50 rounded-2xl">
            <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 mb-2">No payment methods saved</p>
            <p className="text-sm text-slate-400">
              Add a payment method to pay invoices and subscription fees
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {billingPaymentMethods.map((pm) => (
              <div
                key={pm.id}
                className="billing-method-row"
                data-testid={`billing-card-${pm.last4}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center border border-slate-200">
                    <CreditCard className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 capitalize">
                      {pm.brand} •••• {pm.last4}
                      {pm.is_default && (
                        <span className="ml-2 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                          Default
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-slate-500">
                      Expires {pm.exp_month}/{pm.exp_year}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!pm.is_default && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSetDefaultBillingCard(pm.id)}
                      className="rounded-lg"
                    >
                      Set Default
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Remove ${pm.brand} ending in ${pm.last4}`}
                    onClick={() => handleDeleteBillingCard(pm.id)}
                    className="text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Auto-Pay Section */}
      <section className="billing-section billing-autopay" aria-labelledby="billing-autopay-title">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 id="billing-autopay-title" className="billing-title">Auto-Pay</h3>
                <p className="text-sm text-slate-500">
                  Automatically pay invoices using your default payment method
                </p>
              </div>
            </div>
            
            {autoPayEnabled && autoPayDefaultCard && (
              <div className="mt-3 ml-13 flex items-center gap-2 text-sm text-slate-600 bg-slate-50 px-3 py-2 rounded-lg w-fit">
                <CreditCard className="w-4 h-4" />
                <span className="capitalize">{autoPayDefaultCard.brand}</span>
                <span>•••• {autoPayDefaultCard.last4}</span>
                <span className="text-emerald-600 font-medium">Active</span>
              </div>
            )}
            
            {!billingPaymentMethods.length && !autoPayEnabled && (
              <p className="mt-3 text-sm text-amber-600 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Add a payment method first to enable auto-pay
              </p>
            )}
          </div>
          
          <div className="flex items-center">
            <Switch
              aria-label="Auto-Pay"
              checked={autoPayEnabled}
              onCheckedChange={handleToggleAutoPay}
              disabled={autoPayLoading || (!billingPaymentMethods.length && !autoPayEnabled)}
              data-testid="auto-pay-toggle"
            />
          </div>
        </div>
      </section>

      {/* Outstanding Invoices Section */}
      <section className="billing-section billing-invoices" aria-labelledby="billing-invoices-title">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 id="billing-invoices-title" className="billing-title">Outstanding Invoices</h3>
            <p className="text-sm text-slate-500 mt-1">
              View and pay any pending invoices
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => window.location.href = '/admin/invoices'}
          className="rounded-full"
        >
          <Receipt className="w-4 h-4 mr-2" />
          View All Invoices
        </Button>
      </section>

      {/* Add Card Dialog */}
      <Dialog open={!!billingSetupClientSecret} onOpenChange={(open) => {
        if (!open) {
          setBillingSetupClientSecret(null);
          setAddingBillingCard(false);
        }
      }}>
        <DialogContent className="sh-org-billing-settings-batch6 billing-dialog sm:max-w-md"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            const opener = billingOpener.current;
            const canRestoreFocus = (element) => {
              if (!element?.isConnected || element.matches(':disabled') || element.closest('[hidden], [inert]')) return false;
              for (let node = element; node; node = node.parentElement) {
                const style = window.getComputedStyle(node);
                if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return false;
              }
              return true;
            };
            if (canRestoreFocus(opener)) opener.focus();
            if (document.activeElement !== opener || !canRestoreFocus(opener)) {
              const fallback = billingRoot.current;
              if (canRestoreFocus(fallback)) fallback.focus();
            }
          }}>
          <DialogHeader>
            <DialogTitle>Add Payment Method</DialogTitle>
            <DialogDescription>
              Enter your card details to save a payment method for invoices and subscriptions
            </DialogDescription>
          </DialogHeader>
          {billingSetupClientSecret && (
            <Elements stripe={stripePromise} options={{ clientSecret: billingSetupClientSecret }}>
              <BillingCardForm
                clientSecret={billingSetupClientSecret}
                onSuccess={async (paymentMethodId) => {
                  try {
                    await axios.post(
                      `${API}/payments/org/billing/attach-payment-method?payment_method_id=${paymentMethodId}`,
                      {},
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    toast.success("Payment method added successfully");
                    setBillingSetupClientSecret(null);
                    setAddingBillingCard(false);
                    fetchBillingPaymentMethods();
                  } catch (error) {
                    toast.error("Failed to save payment method");
                  }
                }}
                onCancel={() => {
                  setBillingSetupClientSecret(null);
                  setAddingBillingCard(false);
                }}
              />
            </Elements>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BillingTab;
